import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GRANT_EXPIRY_WARNING_DAYS,
  LOW_CREDIT_BALANCE,
  WELCOME_GRANT_CREDITS,
  WELCOME_GRANT_EXPIRY_DAYS,
  WELCOME_GRANT_MAX_PER_USER,
  projectSlugSchema,
  type ProjectSummary,
} from "@zworks/console-contracts";
import { Plus, Zap } from "lucide-react";
import * as React from "react";
import { TenantMark } from "@/components/shell/project-switcher";
import { PageHeader } from "@/components/bespoke";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ApiError, createProject, projectsQuery } from "@/lib/api";
import { formatCredits } from "@/lib/format";

/** Whole days from now until the grant expires. Negative when it already expired. */
function grantDaysLeft(expiresAt: string): number {
  const remaining = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(remaining / (24 * 60 * 60 * 1000));
}

const tileRoleLabels: Record<ProjectSummary["role"], string> = {
  owner: "Owner",
  developer: "Developer",
  approver: "Approver",
  viewer: "Viewer",
};

/**
 * One project tile, as the Picker board draws it: the tenant mark, the name and address, the
 * role, the live counts, and the credits footer. Status is always a labelled badge, never colour
 * alone. An unknown balance stays an Unknown badge, never a zero.
 */
function ProjectTile({ project }: { project: ProjectSummary }) {
  const archived = project.status === "archived";
  const low =
    !archived && project.creditBalance !== null && project.creditBalance <= LOW_CREDIT_BALANCE;
  const expiringDays =
    !archived && project.welcomeGrantExpiresAt
      ? grantDaysLeft(project.welcomeGrantExpiresAt)
      : null;
  const expiring =
    expiringDays !== null && expiringDays >= 0 && expiringDays <= GRANT_EXPIRY_WARNING_DAYS;

  return (
    <Card role="article" aria-label={project.name} className={archived ? "bg-muted/50" : undefined}>
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-3">
          <TenantMark name={project.name} id={project.id} className="size-10 text-sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{project.name}</p>
            <p className="truncate text-xs text-muted-foreground">{project.slug}</p>
          </div>
          {archived ? (
            <Badge variant="secondary">Archived</Badge>
          ) : (
            <Badge variant={project.role === "owner" ? "outline" : "secondary"}>
              {tileRoleLabels[project.role]}
            </Badge>
          )}
        </div>

        {project.description ? (
          <p className="text-sm text-muted-foreground">{project.description}</p>
        ) : null}

        {!archived ? (
          <div className="flex flex-wrap gap-2">
            {project.runningRuns > 0 ? (
              <Badge variant="info">{project.runningRuns} running</Badge>
            ) : null}
            {project.pendingApprovals > 0 ? (
              <Badge variant="warning">{project.pendingApprovals} approvals</Badge>
            ) : null}
            {project.runningRuns === 0 && project.pendingApprovals === 0 ? (
              <Badge variant="secondary">Idle</Badge>
            ) : null}
            {low ? <Badge variant="warning">Credits low</Badge> : null}
            {expiring && expiringDays !== null ? (
              <Badge variant="warning">
                {expiringDays === 0
                  ? "Starter credits expire today"
                  : `Starter credits expire in ${expiringDays} days`}
              </Badge>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center justify-between border-t pt-3 text-sm">
          {archived ? (
            <>
              <span className="text-muted-foreground">Credits</span>
              <span className="text-muted-foreground">Closed</span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-2 text-muted-foreground">
                <Zap className="size-4" aria-hidden />
                Credits
              </span>
              {project.creditBalance === null ? (
                <Badge variant="unknown">Unknown</Badge>
              ) : (
                <b className={low ? "text-warning tabular-nums" : "tabular-nums"}>
                  {formatCredits(project.creditBalance)}
                </b>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

/** "Beacon Telemetry" becomes "beacon-telemetry". The API rechecks it, so this only previews. */
function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

/**
 * The create dialog, as the CreateProject board draws it: the name, the address with its
 * preview, and the grant statement naming which of the 3 starter grants this is. The submit
 * stays disabled until the name and the address are valid, and a taken address is shown inline
 * with the dialog kept open.
 */
function CreateProjectDialog({
  open,
  onOpenChange,
  ownedCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownedCount: number;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [failure, setFailure] = React.useState<{ message: string; requestId?: string } | null>(
    null,
  );

  const atCap = ownedCount >= WELCOME_GRANT_MAX_PER_USER;
  const grantStatement = `This is starter grant ${ownedCount + 1} of ${WELCOME_GRANT_MAX_PER_USER} for your account. The credits are added when the project is created and expire after ${WELCOME_GRANT_EXPIRY_DAYS} days.`;
  const slugIssues = projectSlugSchema.safeParse(slug);
  const slugError =
    slug.length > 0 && !slugIssues.success
      ? (slugIssues.error.issues.at(-1)?.message ?? "Invalid project address.")
      : null;
  const valid = name.trim().length > 0 && slugIssues.success && !atCap;

  function onNameChange(value: string) {
    setName(value);
    if (!slugTouched) setSlug(suggestSlug(value));
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || creating) return;
    setCreating(true);
    setFailure(null);
    try {
      await createProject({ name: name.trim(), slug });
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      onOpenChange(false);
    } catch (error) {
      const apiError =
        error instanceof ApiError
          ? error
          : new ApiError("The request could not be completed.", 500);
      setFailure({
        message: apiError.problem?.detail ?? apiError.message,
        requestId: apiError.requestId,
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="w-[min(33rem,calc(100%-2rem))]">
        <DialogTitle>Create project</DialogTitle>
        <DialogDescription>
          You will be the owner. Projects are separate from each other.
        </DialogDescription>
        <form aria-label="Create project" className="mt-4 flex flex-col gap-4" onSubmit={onSubmit}>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="project-name">
              Project name
            </label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="project-slug">
              Project address
            </label>
            <div className="flex items-center overflow-hidden rounded-md border border-input">
              <span className="flex h-9 items-center border-r border-input bg-muted px-3 text-sm text-muted-foreground">
                zworks.example/p/
              </span>
              <Input
                id="project-slug"
                value={slug}
                onChange={(event) => {
                  setSlugTouched(true);
                  setSlug(event.target.value);
                }}
                autoComplete="off"
                className="rounded-none border-0 shadow-none"
              />
            </div>
            {slugError ? (
              <p role="alert" className="text-[0.8125rem] text-danger">
                {slugError}
              </p>
            ) : null}
          </div>
          <div className="flex gap-3 rounded-[var(--radius-card)] border p-3">
            <Zap className="mt-0.5 size-4 shrink-0 text-accent-text" aria-hidden />
            <div>
              <p className="text-sm font-medium">
                {formatCredits(WELCOME_GRANT_CREDITS)} starter credits
              </p>
              {atCap ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  You have used all {WELCOME_GRANT_MAX_PER_USER} starter grants.
                </p>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">{grantStatement}</p>
              )}
            </div>
          </div>
          {failure ? (
            <div role="alert" className="flex flex-col gap-0.5 text-[0.8125rem]">
              <p className="font-medium text-danger">{failure.message}</p>
              {failure.requestId ? (
                <p className="text-muted-foreground">
                  Reference <span className="font-mono text-foreground">{failure.requestId}</span>
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || creating}>
              {creating ? "Creating…" : "Create project"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The project picker, as the Picker board draws it: one tile per project with its attention
 * states, and the create dialog. Loading, empty, and error are states of the same page.
 */
export function ProjectsPage() {
  const projects = useQuery(projectsQuery);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const list = projects.data ?? [];
  const ownedCount = list.filter((project) => project.role === "owner").length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Your projects"
        actions={
          <Button type="button" onClick={() => setDialogOpen(true)}>
            <Plus aria-hidden />
            Create project
          </Button>
        }
      />

      {projects.isPending ? (
        <LoadingState label="Loading projects" rows={3} />
      ) : projects.isError ? (
        <ErrorState
          title="Projects could not be loaded"
          description={
            projects.error instanceof ApiError
              ? (projects.error.problem?.detail ?? projects.error.message)
              : "The request could not be completed."
          }
          requestId={
            projects.error instanceof ApiError ? (projects.error.requestId ?? "unknown") : "unknown"
          }
          onRetry={() => void projects.refetch()}
        />
      ) : list.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="Create your first project. You will be its owner, and the starter credits arrive with it."
          action={{ label: "Create your first project", onClick: () => setDialogOpen(true) }}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((project) => (
            <ProjectTile key={project.id} project={project} />
          ))}
        </div>
      )}

      <CreateProjectDialog open={dialogOpen} onOpenChange={setDialogOpen} ownedCount={ownedCount} />
    </div>
  );
}
